import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Select,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Title, useDataProvider } from 'react-admin';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ComparisonSankeyChart from './ComparisonSankeyChart';
import ComparisonCveTable from './ComparisonCveTable';

interface CustomDataProvider {
  custom: (params: any) => Promise<any>;
  getUnique: (resource: string, params: any) => Promise<any>;
}

export interface ComparisonCve {
  name: string;
  severity: string;
  cvssScore: number | null;
  imageTag: string;
  imageRepository: string;
  availableFixedImageBuildTag: string | null;
  availableFixedReleaseVersion: string | null;
}

interface ComparisonBucket {
  label: string;
  count: number;
  cves: ComparisonCve[];
}

interface ComparisonData {
  version1: { name: string; version: string; totalCves: number };
  version2: { name: string; version: string; totalCves: number };
  comparison: {
    // present in version1, gone by version2 (remediated)
    fixed: ComparisonBucket;
    // present in both versions (still vulnerable)
    unfixed: ComparisonBucket;
    // present only in version2 (newly introduced)
    new: ComparisonBucket;
  };
}

export type ComparisonStatus = 'fixed' | 'unfixed' | 'new';

export interface ComparisonCveRow extends ComparisonCve {
  comparisonStatus: ComparisonStatus;
}

const ReleaseComparison: React.FC = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider() as any;
  const [releaseNames, setReleaseNames] = useState<string[]>([]);
  const [version1Choices, setVersion1Choices] = useState<string[]>([]);
  const [version2Choices, setVersion2Choices] = useState<string[]>([]);

  const [releaseName, setReleaseName] = useState('');
  const [version1, setVersion1] = useState('');
  const [version2, setVersion2] = useState('');

  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReleaseNames = async () => {
      try {
        const names = await dataProvider.getUnique('cve/releases', {
          field: 'name',
        });
        setReleaseNames(names as string[]);
        if (names.length > 0) {
          setReleaseName(names[0]);
          await fetchVersions(names[0]);
        }
      } catch (err) {
        setError('Failed to load releases');
      }
    };
    fetchReleaseNames();
  }, [dataProvider]);

  const fetchVersions = async (name: string) => {
    try {
      const versions = await dataProvider.getUnique('cve/releases', {
        field: 'version',
        filter: { name },
      });
      setVersion1Choices(versions as string[]);
      setVersion2Choices(versions as string[]);
      if (versions.length > 0) {
        setVersion1(versions.length > 1 ? versions[1] : versions[0]);
        setVersion2(versions[0]);
      }
    } catch (err) {
      setError('Failed to load versions');
    }
  };

  const handleCompare = async () => {
    if (!releaseName || !version1 || !version2 || version1 === version2) {
      setError('Please select two different versions to compare');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const url = `cve/comparison/compare-releases?releaseName1=${encodeURIComponent(
        releaseName
      )}&releaseVersion1=${encodeURIComponent(version1)}&releaseName2=${encodeURIComponent(
        releaseName
      )}&releaseVersion2=${encodeURIComponent(version2)}`;

      const response = await dataProvider.custom(url, 'GET');
      const data = response?.data;

      if (!data?.version1) {
        throw new Error('Invalid response from comparison API');
      }

      setComparisonData(data as ComparisonData);
    } catch (err) {
      console.error('Comparison error:', err);
      setError(err instanceof Error ? err.message : 'Failed to compare releases');
    } finally {
      setLoading(false);
    }
  };

  const csvEscape = (value: string | number | null | undefined) => {
    const str = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const downloadCsv = (csv: string, filename: string) => {
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    );
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const CVE_CSV_HEADERS = [
    'name',
    'severity',
    'cvssScore',
    'imageRepository',
    'imageTag',
    'availableFixedImageBuildTag',
    'availableFixedReleaseVersion',
  ] as const;

  const exportComparisonCsv = () => {
    if (!comparisonData) return;
    const headers = ['comparisonStatus', ...CVE_CSV_HEADERS];
    const rows: Array<[ComparisonStatus, ComparisonCve]> = [
      ...comparisonData.comparison.fixed.cves.map(
        (c): [ComparisonStatus, ComparisonCve] => ['fixed', c]
      ),
      ...comparisonData.comparison.unfixed.cves.map(
        (c): [ComparisonStatus, ComparisonCve] => ['unfixed', c]
      ),
      ...comparisonData.comparison.new.cves.map(
        (c): [ComparisonStatus, ComparisonCve] => ['new', c]
      ),
    ];
    const csv = [headers.join(',')]
      .concat(
        rows.map(([status, c]) =>
          [
            csvEscape(status),
            ...CVE_CSV_HEADERS.map((header) => csvEscape(c[header])),
          ].join(',')
        )
      )
      .join('\n');
    downloadCsv(
      csv,
      `cve-comparison-${comparisonData.version1.version}-vs-${comparisonData.version2.version}.csv`
    );
  };

  const comparisonRows: ComparisonCveRow[] = comparisonData
    ? [
        ...comparisonData.comparison.fixed.cves.map(
          (c): ComparisonCveRow => ({ ...c, comparisonStatus: 'fixed' })
        ),
        ...comparisonData.comparison.unfixed.cves.map(
          (c): ComparisonCveRow => ({ ...c, comparisonStatus: 'unfixed' })
        ),
        ...comparisonData.comparison.new.cves.map(
          (c): ComparisonCveRow => ({ ...c, comparisonStatus: 'new' })
        ),
      ]
    : [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/cve/releases')}
        >
          Back to Releases
        </Button>
      </Box>
      <Title title="Compare Releases" />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Versions to Compare
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Select
              value={releaseName}
              onChange={(e) => {
                setReleaseName(e.target.value);
                fetchVersions(e.target.value);
              }}
              sx={{ width: '250px' }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Product
              </MenuItem>
              {releaseNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={version1}
              onChange={(e) => setVersion1(e.target.value)}
              sx={{ width: '150px' }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Version 1
              </MenuItem>
              {version1Choices.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>

            <Typography sx={{ alignSelf: 'center', fontWeight: 'bold' }}>
              vs
            </Typography>

            <Select
              value={version2}
              onChange={(e) => setVersion2(e.target.value)}
              sx={{ width: '150px' }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Version 2
              </MenuItem>
              {version2Choices.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant="contained"
              onClick={handleCompare}
              disabled={loading || !version1 || !version2 || version1 === version2}
            >
              {loading ? 'Comparing...' : 'Compare'}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {loading && <CircularProgress />}
        </CardContent>
      </Card>

      {comparisonData && !loading && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportComparisonCsv}
            >
              Export Comparison CSV
            </Button>
          </Box>
          <Box sx={{ mb: 3 }}>
            <ComparisonSankeyChart
              version1Label={`${comparisonData.version1.name} ${comparisonData.version1.version}`}
              version2Label={`${comparisonData.version2.name} ${comparisonData.version2.version}`}
              fixedCount={comparisonData.comparison.fixed.count}
              unfixedCount={comparisonData.comparison.unfixed.count}
              newCount={comparisonData.comparison.new.count}
            />
          </Box>

          <ComparisonCveTable rows={comparisonRows} />
        </>
      )}
    </Box>
  );
};

export default ReleaseComparison;
